import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Clock, AlertTriangle, X, Zap, ArrowRight, ShieldOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * TrialBanner — shown at the top of the Super Admin dashboard.
 * - If trial is active + > 3 days: soft info bar (blue)
 * - If trial is active + ≤ 3 days: urgent warning bar (amber)
 * - If trial is expired: full-screen takeover overlay (red)
 */
const TrialBanner = () => {
  const { subscription, isTrialExpired, daysRemaining } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const appName = process.env.REACT_APP_APP_NAME || 'CRM Automax';

  if (!subscription) return null;
  
  const isCustom = subscription.plan !== 'free_trial';
  
  if (!isCustom && dismissed) return null; // Only allow dismissing trial banners, keep custom ones or handle differently
  if (isCustom && dismissed) return null; // Actually, let's allow dismissing both if desired, but custom feels like it should be more persistent if it's a "badge of honor" or info.

  // ── Full-screen expired overlay ────────────────────────────────────────────
  if (isTrialExpired) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Inter', sans-serif",
      }}>
        <style>{`
          @keyframes scaleIn { from{opacity:0;transform:scale(0.9);}to{opacity:1;transform:scale(1);} }
          @keyframes pulse-ring-red {
            0%   { transform: scale(1);   opacity: 0.5; }
            100% { transform: scale(1.6); opacity: 0; }
          }
        `}</style>
        <div style={{
          width: '100%', maxWidth: '480px', margin: '24px',
          background: 'linear-gradient(145deg, #0f172a, #1a0a0a)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '28px', padding: '52px 44px',
          textAlign: 'center', animation: 'scaleIn 0.4s ease',
          boxShadow: '0 40px 80px rgba(0,0,0,0.7)',
        }}>
          {/* Pulsing icon */}
          <div style={{ position: 'relative', display: 'inline-flex', marginBottom: '28px' }}>
            <div style={{
              position: 'absolute', inset: '-10px', borderRadius: '50%',
              border: '1px solid rgba(239,68,68,0.4)',
              animation: 'pulse-ring-red 2s ease-out infinite',
            }} />
            <div style={{
              width: '80px', height: '80px', borderRadius: '50%',
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ShieldOff size={40} color="#f87171" />
            </div>
          </div>

          <h2 style={{ fontSize: '26px', fontWeight: '900', color: '#f8fafc', letterSpacing: '-0.02em', marginBottom: '12px' }}>
            Free Trial Expired
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '15px', lineHeight: 1.7, marginBottom: '10px' }}>
            Your {process.env.REACT_APP_APP_NAME || 'CRM Automax'} free trial has ended.
          </p>
          <p style={{ color: '#64748b', fontSize: '13px', lineHeight: 1.6, marginBottom: '32px' }}>
            Your data is safe. Upgrade to a plan to restore full access for you and your team.
            Your employees cannot log in until a plan is activated.
          </p>

          {/* What's locked */}
          <div style={{
            background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
            borderRadius: '14px', padding: '16px', marginBottom: '28px', textAlign: 'left',
          }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
              Currently locked for your team
            </div>
            {['Lead Management', 'Employee Access', 'Reports & Analytics', 'Notifications'].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', color: '#f87171', fontSize: '13px' }}>
                <X size={13} /> {item}
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate('/admin/pricing')}
            style={{
              width: '100%', padding: '16px',
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: 'white', border: 'none', borderRadius: '14px',
              fontSize: '16px', fontWeight: '700', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              boxShadow: '0 10px 28px -4px rgba(37,99,235,0.5)',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Zap size={18} fill="white" /> View Plans &amp; Upgrade <ArrowRight size={17} />
          </button>

          <p style={{ marginTop: '20px', fontSize: '12px', color: '#334155' }}>
            Need help? Contact{' '}
            <a href="mailto:support@crmAutomax.com" style={{ color: '#3b82f6', textDecoration: 'none' }}>
              support@{appName.toLowerCase().replace(/\s/g, '')}.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  // ── Already dismissed the soft banner ─────────────────────────────────────
  if (dismissed) return null;

  const isUrgent = !isCustom && daysRemaining !== null && daysRemaining <= 3;

  return (
    <div style={{
      width: '100%',
      background: isCustom 
        ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.05), rgba(30, 41, 59, 0.03))'
        : (isUrgent
            ? 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(217,119,6,0.08))'
            : 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(37,99,235,0.06))'),
      borderBottom: `1px solid ${isCustom ? 'rgba(15, 23, 42, 0.1)' : (isUrgent ? 'rgba(245,158,11,0.25)' : 'rgba(59,130,246,0.2)')}`,
      padding: '12px 24px',
      display: 'flex', alignItems: 'center', gap: '12px',
      fontFamily: "'Inter', sans-serif",
    }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '10px', flexShrink: 0,
        background: isCustom ? 'rgba(15, 23, 42, 0.1)' : (isUrgent ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)'),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {isCustom 
          ? <Zap size={17} color="#0f172a" fill="#0f172a" />
          : (isUrgent
              ? <AlertTriangle size={17} color="#f59e0b" />
              : <Clock size={17} color="#60a5fa" />
            )
        }
      </div>

      <div style={{ flex: 1 }}>
        <span style={{ fontSize: '13px', fontWeight: '700', color: isCustom ? '#0f172a' : (isUrgent ? '#fbbf24' : '#93c5fd') }}>
          {isCustom ? `Exclusive Plan Active: ${subscription.plan} — ` : (isUrgent ? '⚠️ Trial ending soon! ' : '🎉 Free Trial Active — ')}
        </span>
        <span style={{ fontSize: '13px', color: isCustom ? '#475569' : (isUrgent ? '#d97706' : '#64748b') }}>
          {isCustom 
            ? `${subscription.maxUsers} User Seats | ${subscription.daysRemaining} days remaining`
            : (daysRemaining !== null
                ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`
                : 'Trial active')
          }
          {!isCustom && isUrgent ? ' — Upgrade now to avoid losing access for your team.' : ''}
        </span>
      </div>

      <button
        onClick={() => navigate('/admin/pricing')}
        style={{
          background: isCustom 
            ? '#0f172a'
            : (isUrgent
                ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                : 'linear-gradient(135deg, #3b82f6, #2563eb)'),
          color: 'white', border: 'none',
          padding: '7px 16px', borderRadius: '8px',
          fontSize: '12px', fontWeight: '700', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '5px',
          whiteSpace: 'nowrap', flexShrink: 0,
          transition: 'all 0.2s ease',
        }}
        onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
        onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
      >
        {isCustom ? 'Plan Details' : (isUrgent ? 'Upgrade Now' : 'View Plans')} <ArrowRight size={12} />
      </button>

      <button
        onClick={() => setDismissed(true)}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#475569', padding: '4px', borderRadius: '6px',
          display: 'flex', alignItems: 'center', flexShrink: 0,
          transition: 'color 0.2s',
        }}
        onMouseOver={e => e.currentTarget.style.color = '#94a3b8'}
        onMouseOut={e => e.currentTarget.style.color = '#475569'}
        title="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default TrialBanner;
